import ExcelJS from 'exceljs'
import JSZip from 'jszip'
import sealImg from '@/assets/images/seal.png'
import {
  WEEKLY_SAFETY_NOTE_FIELDS,
  WEEKLY_SAFETY_EVALUATION_TITLE,
  WEEKLY_SAFETY_EVALUATION_OPTIONS
} from '@/config/weeklyInspectionConfig'

const COL_COUNT = 8
const EMU_PER_PIXEL = 9525
const SEAL_WIDTH_PX = 145
const SEAL_HEIGHT_PX = 130
const CHAR_WIDTH_PX = 7
const PT_TO_PX = 96 / 72
const DEFAULT_ROW_HEIGHT_PT = 15
const DEFAULT_ROW_HEIGHT_PX = DEFAULT_ROW_HEIGHT_PT * PT_TO_PX

const DAILY_SECTION_TITLE_MAP = {
  doorStatus: '1、电梯开关门情况',
  controlPanel: '2、电梯控制面板和按钮操作情况',
  startStop: '3、电梯运行情况',
  internalSafety: '4、电梯内部安全情况',
  externalSafety: '5、电梯外部安全情况',
  managedEquipment: '6、托管配套设备/机房'
}

const DAILY_EXPORT_MANAGED_EQUIPMENT_ITEMS = [
  { key: 'monitorAngle', label: '监控角度是否正常' },
  { key: 'sdCardStatus', label: 'SD卡/物联卡是否正常' },
  { key: 'upsStatus', label: 'UPS电源是否正常' },
  { key: 'alarmDeviceStatus', label: '报警装置是否正常' }
]

const DAILY_EXPORT_MACHINE_ROOM_ITEMS = [
  { key: 'machineRoomEnvironment', label: '机房环境是否良好' },
  { key: 'machineRoomDoorLighting', label: '机房门窗、灯照明是否正常' },
  { key: 'machineRoomVentilator', label: '排风扇是否正常' },
  { key: 'machineRoomTemperature', label: '机房温度是否正常' }
]

function buildMergedManagedEquipmentSection(managedSection, machineRoomSection) {
  return {
    key: 'managedEquipment',
    title: DAILY_SECTION_TITLE_MAP.managedEquipment,
    items: [
      ...(managedSection?.items || DAILY_EXPORT_MANAGED_EQUIPMENT_ITEMS),
      ...(machineRoomSection?.items || DAILY_EXPORT_MACHINE_ROOM_ITEMS)
    ]
  }
}

function distributeDailySections(sections) {
  const byKey = Object.fromEntries(sections.map((s) => [s.key, s]))
  return [
    [byKey.doorStatus, byKey.internalSafety].filter(Boolean),
    [byKey.controlPanel, byKey.externalSafety].filter(Boolean),
    [byKey.startStop, byKey.managedEquipment].filter(Boolean)
  ]
}

const SECTIONS_WITH_SPACER_BEFORE = new Set(['internalSafety', 'externalSafety', 'managedEquipment'])

function buildDailySectionColumn(sections, formValues) {
  const rows = []
  sections.forEach((section) => {
    if (SECTIONS_WITH_SPACER_BEFORE.has(section.key)) {
      rows.push(['', ''])
    }
    const title = DAILY_SECTION_TITLE_MAP[section.key] || section.title
    rows.push([title, ''])
    section.items.forEach((item) => {
      rows.push([item.label, formValues[item.key] ?? '是'])
    })
  })
  return rows
}

function mergeDailySectionColumns(columnGroups, formValues) {
  const columns = columnGroups.map((group) => buildDailySectionColumn(group, formValues))
  const maxLen = Math.max(1, ...columns.map((col) => col.length))
  const rows = []
  for (let i = 0; i < maxLen; i++) {
    const cells = []
    columns.forEach((col) => {
      const row = col[i] || ['', '']
      cells.push(row[0] || '', row[1] || '')
    })
    rows.push(cells)
  }
  return rows
}

function getDailyExportSections(checklistSections) {
  const sections = checklistSections.map((section) => ({
    ...section,
    title: DAILY_SECTION_TITLE_MAP[section.key] || section.title
  }))

  const managedSection = sections.find((item) => item.key === 'managedEquipment')
  const machineRoomSection = sections.find((item) => item.key === 'machineRoom')
  const mergedSection = buildMergedManagedEquipmentSection(managedSection, machineRoomSection)

  const withoutManagedBlocks = sections.filter(
    (item) => item.key !== 'managedEquipment' && item.key !== 'machineRoom'
  )

  return [...withoutManagedBlocks, mergedSection]
}

function writeSectionHeaderRow(sheet, rowNum, title) {
  sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
  const cell = sheet.getCell(rowNum, 1)
  cell.value = title
  cell.font = { bold: true, size: 12 }
  cell.alignment = { horizontal: 'center', vertical: 'middle' }
  setBorder(cell)
  return rowNum + 1
}

function writeLabelValuePairsRow(sheet, rowNum, pairs) {
  const row = sheet.getRow(rowNum)
  pairs.forEach(([label, value], index) => {
    row.getCell(index * 2 + 1).value = label
    row.getCell(index * 2 + 2).value = value ?? '-'
    setBorder(row.getCell(index * 2 + 1))
    setBorder(row.getCell(index * 2 + 2))
  })
  return rowNum + 1
}

function getImageExtension(url) {
  const lower = String(url).split('?')[0].toLowerCase()
  if (lower.endsWith('.png')) return 'png'
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'jpeg'
  if (lower.endsWith('.gif')) return 'gif'
  return 'png'
}

async function patchLastImageAnchor(buffer, widthPx, heightPx) {
  const zip = await JSZip.loadAsync(buffer)
  const drawingPath = Object.keys(zip.files).find((name) => /xl\/drawings\/drawing\d+\.xml$/.test(name))
  if (!drawingPath) return buffer

  const xml = await zip.file(drawingPath).async('string')
  const patchedXml = patchLastImageAnchorXml(xml, widthPx, heightPx)
  if (patchedXml === xml) return buffer

  zip.file(drawingPath, patchedXml)
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' })
}

function patchLastImageAnchorXml(xml, widthPx, heightPx) {
  const emuW = Math.round(widthPx * EMU_PER_PIXEL)
  const emuH = Math.round(heightPx * EMU_PER_PIXEL)
  const anchorRegex = /<xdr:(?:one|two)CellAnchor[\s\S]*?<\/xdr:(?:one|two)CellAnchor>/g
  const matches = [...xml.matchAll(anchorRegex)]
  if (!matches.length) return xml

  const absoluteAnchors = matches.filter((m) => m[0].includes('editAs="absolute"'))
  const last = absoluteAnchors.length ? absoluteAnchors[absoluteAnchors.length - 1] : matches[matches.length - 1]
  let anchor = last[0]

  if (anchor.includes('twoCellAnchor')) {
    const from = anchor.match(/<xdr:from>[\s\S]*?<\/xdr:from>/)?.[0]
    const pic = anchor.match(/<xdr:pic>[\s\S]*?<\/xdr:pic>/)?.[0]
    if (!from || !pic) return xml
    anchor = `<xdr:oneCellAnchor editAs="absolute">${from}<xdr:ext cx="${emuW}" cy="${emuH}"/>${pic}<xdr:clientData/></xdr:oneCellAnchor>`
  } else {
    anchor = anchor.replace(
      /<xdr:ext cx="\d+" cy="\d+"\/>/,
      `<xdr:ext cx="${emuW}" cy="${emuH}"/>`
    )
  }

  anchor = anchor
    .replace(
      /<a:xfrm><a:off x="0" y="0"\/><a:ext cx="0" cy="0"\/><\/a:xfrm>/g,
      `<a:xfrm><a:off x="0" y="0"/><a:ext cx="${emuW}" cy="${emuH}"/></a:xfrm>`
    )
    .replace(/<a:stretch><a:fillRect\/?><\/a:stretch>/g, '<a:srcRect l="0" t="0" r="0" b="0"/>')

  return xml.slice(0, last.index) + anchor + xml.slice(last.index + last[0].length)
}

function getColumnWidthPx(sheet, colIndex) {
  const col = sheet.getColumn(colIndex)
  return (col.width || 8.43) * CHAR_WIDTH_PX
}

function getRowHeightPx(sheet, rowIndex) {
  const row = sheet.getRow(rowIndex)
  return (row.height || DEFAULT_ROW_HEIGHT_PT) * PT_TO_PX
}

function ensureMinimumAreaHeight(sheet, startRow, endRow, minHeightPx) {
  let totalHeightPx = 0
  for (let row = startRow; row <= endRow; row++) {
    totalHeightPx += getRowHeightPx(sheet, row)
  }
  if (totalHeightPx >= minHeightPx) return

  const extraPx = minHeightPx - totalHeightPx
  const rowCount = endRow - startRow + 1
  const extraPerRowPt = extraPx / rowCount / PT_TO_PX
  for (let row = startRow; row <= endRow; row++) {
    const sheetRow = sheet.getRow(row)
    sheetRow.height = (sheetRow.height || DEFAULT_ROW_HEIGHT_PT) + extraPerRowPt
  }
}

function calcCenteredImageAnchor(sheet, {
  startCol,
  endCol,
  startRow,
  endRow,
  widthPx,
  heightPx
}) {
  const colWidths = []
  let totalWidthPx = 0
  for (let col = startCol; col <= endCol; col++) {
    const width = getColumnWidthPx(sheet, col)
    colWidths.push(width)
    totalWidthPx += width
  }

  const rowHeights = []
  let totalHeightPx = 0
  for (let row = startRow; row <= endRow; row++) {
    const height = getRowHeightPx(sheet, row)
    rowHeights.push(height)
    totalHeightPx += height
  }

  const leftOffsetPx = Math.max(0, (totalWidthPx - widthPx) / 2)
  const topOffsetPx = Math.max(0, (totalHeightPx - heightPx) / 2)

  let accPx = 0
  let colIndex = startCol
  let colOffsetPx = 0
  for (let i = 0; i < colWidths.length; i++) {
    const width = colWidths[i]
    if (accPx + width > leftOffsetPx || i === colWidths.length - 1) {
      colIndex = startCol + i
      colOffsetPx = Math.max(0, leftOffsetPx - accPx)
      break
    }
    accPx += width
  }

  accPx = 0
  let rowIndex = startRow
  let rowOffsetPx = 0
  for (let i = 0; i < rowHeights.length; i++) {
    const height = rowHeights[i]
    if (accPx + height > topOffsetPx || i === rowHeights.length - 1) {
      rowIndex = startRow + i
      rowOffsetPx = Math.max(0, topOffsetPx - accPx)
      break
    }
    accPx += height
  }

  const colWidth = colWidths[colIndex - startCol] || 1
  const rowHeight = rowHeights[rowIndex - startRow] || DEFAULT_ROW_HEIGHT_PX

  return {
    col: colIndex - 1 + colOffsetPx / colWidth,
    row: rowIndex - 1 + rowOffsetPx / rowHeight
  }
}

async function addCenteredSeal(workbook, sheet, area) {
  const sealBuffer = await fetchImageBuffer(sealImg)
  const sealId = workbook.addImage({ buffer: sealBuffer, extension: 'png' })
  const anchor = calcCenteredImageAnchor(sheet, {
    ...area,
    widthPx: SEAL_WIDTH_PX,
    heightPx: SEAL_HEIGHT_PX
  })
  sheet.addImage(sealId, {
    tl: anchor,
    ext: { width: SEAL_WIDTH_PX, height: SEAL_HEIGHT_PX },
    editAs: 'absolute'
  })
  return true
}

async function fetchImageBuffer(url) {
  const response = await fetch(url, { mode: 'cors' })
  if (!response.ok) throw new Error('图片加载失败')
  return response.arrayBuffer()
}

function setBorder(cell) {
  cell.border = {
    top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
  }
  cell.alignment = { ...(cell.alignment || {}), wrapText: true, vertical: 'middle' }
}

function downloadWorkbook(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

/**
 * 按日管控页面模板导出 Excel
 */
export async function exportDailyControlReport({
  formattedReportDate,
  currentTime,
  basicInfo,
  formValues,
  summaryFields,
  checklistSections,
  todayEvents,
  eventStr,
  workOrderRecords,
  formatEventDisplay,
  filename
}) {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('海康智慧电梯监测日管控')

  sheet.columns = [
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 20 },
    { width: 26 }
  ]

  let rowNum = 1

  // 标题 + 系统时间
  sheet.mergeCells(rowNum, 1, rowNum + 1, 7)
  const titleCell = sheet.getCell(rowNum, 1)
  titleCell.value = '海康智慧电梯监测日管控'
  titleCell.font = { size: 20, bold: true }
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
  setBorder(titleCell)

  sheet.getCell(rowNum, 8).value = '系统时间'
  sheet.getCell(rowNum, 8).font = { bold: true, size: 11 }
  sheet.getCell(rowNum, 8).alignment = { horizontal: 'center', vertical: 'middle' }
  setBorder(sheet.getCell(rowNum, 8))

  sheet.getCell(rowNum + 1, 8).value = currentTime || formattedReportDate || ''
  sheet.getCell(rowNum + 1, 8).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  setBorder(sheet.getCell(rowNum + 1, 8))

  sheet.getRow(rowNum).height = 24
  sheet.getRow(rowNum + 1).height = 24
  rowNum += 3

  // 电梯基本信息
  rowNum = writeSectionHeaderRow(sheet, rowNum, '电梯基本信息')
  rowNum = writeLabelValuePairsRow(sheet, rowNum, [
    ['项目地址', basicInfo.address || '-'],
    ['注册代码', basicInfo.registrationCode || '-'],
    ['电梯工号', basicInfo.elevatorNumber || '-']
  ])
  const basicRow1Num = rowNum - 1

  rowNum = writeLabelValuePairsRow(sheet, rowNum, [
    ['使用登记证', basicInfo.usageCertificate || '-'],
    ['维保单位', basicInfo.maintenanceUnit || '-'],
    ['安全管理员', basicInfo.safetyAdmin || '-']
  ])
  const basicRow2Num = rowNum - 1

  sheet.mergeCells(basicRow1Num, 7, basicRow2Num, 7)
  sheet.mergeCells(basicRow1Num, 8, basicRow2Num, 8)
  sheet.getCell(basicRow1Num, 7).value = '手写签名'
  sheet.getCell(basicRow1Num, 7).alignment = { horizontal: 'center', vertical: 'middle' }
  sheet.getCell(basicRow1Num, 8).alignment = { horizontal: 'center', vertical: 'middle' }
  setBorder(sheet.getCell(basicRow1Num, 7))
  setBorder(sheet.getCell(basicRow1Num, 8))
  sheet.getRow(basicRow1Num).height = 40
  sheet.getRow(basicRow2Num).height = 40

  if (basicInfo.signature) {
    try {
      const imageBuffer = await fetchImageBuffer(basicInfo.signature)
      const imageId = workbook.addImage({
        buffer: imageBuffer,
        extension: getImageExtension(basicInfo.signature)
      })
      sheet.addImage(imageId, {
        tl: { col: 7.15, row: basicRow1Num - 0.85 },
        ext: { width: 100, height: 68 }
      })
    } catch (e) {
      sheet.getCell(basicRow1Num, 8).value = basicInfo.signature
    }
  }
  rowNum += 2

  // 日管控结果
  rowNum = writeSectionHeaderRow(sheet, rowNum, '日管控结果')

  const summaryRow = sheet.getRow(rowNum)
  summaryFields.forEach((field, index) => {
    const startCol = index * 2 + 1
    sheet.mergeCells(rowNum, startCol, rowNum, startCol + 1)
    const cell = summaryRow.getCell(startCol)
    cell.value = `${field.label}：${formValues[field.key] || ''}`
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    setBorder(cell)
  })
  rowNum += 2

  const exportSections = getDailyExportSections(checklistSections)
  const checklistStartRow = rowNum
  const checklistRows = mergeDailySectionColumns(
    distributeDailySections(exportSections),
    formValues
  )

  checklistRows.forEach((cells) => {
    const row = sheet.getRow(rowNum)
    for (let col = 0; col < 2; col++) {
      row.getCell(col * 2 + 1).value = cells[col * 2] || ''
      row.getCell(col * 2 + 2).value = cells[col * 2 + 1] || ''
      setBorder(row.getCell(col * 2 + 1))
      setBorder(row.getCell(col * 2 + 2))
    }
    row.getCell(5).value = cells[4] || ''
    sheet.mergeCells(rowNum, 6, rowNum, 8)
    row.getCell(6).value = cells[5] || ''
    setBorder(row.getCell(5))
    setBorder(row.getCell(6))
    rowNum++
  })

  rowNum++

  // 底部：今日触发事件 | 工单记录
  sheet.mergeCells(rowNum, 1, rowNum, 4)
  sheet.mergeCells(rowNum, 5, rowNum, 8)
  sheet.getCell(rowNum, 1).value = '今日触发事件'
  sheet.getCell(rowNum, 5).value = '工单记录'
  sheet.getCell(rowNum, 1).font = { bold: true, size: 12 }
  sheet.getCell(rowNum, 5).font = { bold: true, size: 12 }
  sheet.getCell(rowNum, 1).alignment = { horizontal: 'center', vertical: 'middle' }
  sheet.getCell(rowNum, 5).alignment = { horizontal: 'center', vertical: 'middle' }
  setBorder(sheet.getCell(rowNum, 1))
  setBorder(sheet.getCell(rowNum, 5))
  rowNum++

  const eventText =
    eventStr ||
    (todayEvents.length
      ? todayEvents.map((evt) => `${evt.createtime || ''} ${formatEventDisplay(evt)}`.trim()).join('\n')
      : '无')

  const orderRows = []
  if (workOrderRecords && workOrderRecords.length) {
    orderRows.push(['生成时间', '记录类型', '紧急程度', '处理状态'])
    workOrderRecords.forEach((order) => {
      orderRows.push([
        order.createTime || '-',
        order.orderType || '-',
        order.urgency || '-',
        order.status || '-'
      ])
    })
  } else {
    orderRows.push(['无'])
  }

  const footerStartRow = rowNum
  const footerLen = Math.max(1, orderRows.length)
  for (let i = 0; i < footerLen; i++) {
    if (i === 0) {
      sheet.mergeCells(rowNum, 1, rowNum + footerLen - 1, 4)
      const eventCell = sheet.getCell(rowNum, 1)
      eventCell.value = eventText
      eventCell.alignment = { wrapText: true, vertical: 'top' }
      setBorder(eventCell)
    }

    const right = orderRows[i] || ['']
    const row = sheet.getRow(rowNum)
    row.getCell(5).value = right[0] || ''
    row.getCell(6).value = right[1] || ''
    row.getCell(7).value = right[2] || ''
    row.getCell(8).value = right[3] || ''
    row.eachCell((cell, colNumber) => {
      if (colNumber >= 5) setBorder(cell)
    })
    rowNum++
  }

  const eventLineCount = Math.max(3, String(eventText).split('\n').length)
  sheet.getRow(footerStartRow).height = Math.min(180, eventLineCount * 16 + 12)

  let sealAdded = false
  try {
    const checklistEndRow = checklistStartRow + checklistRows.length - 1
    sealAdded = await addCenteredSeal(workbook, sheet, {
      startCol: 1,
      endCol: COL_COUNT,
      startRow: checklistStartRow,
      endRow: checklistEndRow
    })
  } catch (e) {
    console.warn('印章加载失败', e)
  }

  let buffer = await workbook.xlsx.writeBuffer()
  if (sealAdded) {
    buffer = await patchLastImageAnchor(buffer, SEAL_WIDTH_PX, SEAL_HEIGHT_PX)
  }
  downloadWorkbook(buffer, filename || '海康智慧电梯监测日管控.xlsx')
}

/** @deprecated 使用 exportDailyControlReport */
export function exportDailyControlExcel() {
  console.warn('exportDailyControlExcel 已废弃，请使用 exportDailyControlReport')
}

function buildEventSectionRows({ textStr, events, formatEventDisplay, emptyText }) {
  if (textStr) return [[textStr]]
  if (events && events.length) {
    const rows = [['触发时间', '事件内容', '项目地址']]
    events.forEach((evt) => {
      rows.push([
        evt.createtime || '-',
        formatEventDisplay(evt),
        evt.community_name || '-'
      ])
    })
    return rows
  }
  return [[emptyText || '无']]
}

function buildThisWeekSectionRows({
  eventStr,
  thisWeekEvents,
  thisWeekEventStats,
  formatEventDisplay,
  thisPeriodTotalLabel = '本周触发总数',
  emptyThisPeriodText = '本周暂无触发记录'
}) {
  if (eventStr) return [[eventStr]]
  if (thisWeekEvents && thisWeekEvents.length) {
    const rows = []
    const statParts = [`${thisPeriodTotalLabel}：${thisWeekEvents.length}`]
    ;(thisWeekEventStats || []).forEach((item) => {
      statParts.push(`${item.name}：${item.count}`)
    })
    rows.push([statParts.join('  ')])
    rows.push(['触发时间', '事件内容', '项目地址'])
    thisWeekEvents.forEach((evt) => {
      rows.push([
        evt.createtime || '-',
        formatEventDisplay(evt),
        evt.community_name || '-'
      ])
    })
    return rows
  }
  return [[emptyThisPeriodText]]
}

function buildWorkOrderSectionRows(orders) {
  if (!orders || !orders.length) return [['无']]
  const rows = [['生成时间', '项目地址', '记录类型', '紧急程度']]
  orders.forEach((order) => {
    rows.push([
      order.createTime || '-',
      order.communityAddress || order.areaId || '-',
      order.orderType || '-',
      order.urgency || '-'
    ])
  })
  return rows
}

function writeParallelWorkOrderSections(
  sheet,
  rowNum,
  lastWeekLabel,
  thisWeekLabel,
  lastWeekWorkOrderRecords,
  thisWeekWorkOrderRecords,
  lastWorkOrderSectionPrefix = '上周工单未处理记录',
  thisWorkOrderSectionPrefix = '本周工单未处理记录'
) {
  writePeriodSectionTitles(
    sheet,
    rowNum,
    lastWeekLabel,
    thisWeekLabel,
    lastWorkOrderSectionPrefix,
    thisWorkOrderSectionPrefix
  )
  rowNum++
  const leftRows = buildWorkOrderSectionRows(lastWeekWorkOrderRecords)
  const rightRows = buildWorkOrderSectionRows(thisWeekWorkOrderRecords)
  return writeParallelRows(sheet, rowNum, leftRows, rightRows)
}

function calcMergedTextRowHeight(text, columnWidths) {
  if (!text) return 22
  const str = String(text)
  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0)
  const charsPerLine = Math.max(8, Math.floor(totalWidth * 0.65))
  const lineBreaks = (str.match(/\n/g) || []).length
  const wrappedLines = Math.ceil(str.length / charsPerLine)
  const lines = Math.max(1, wrappedLines + lineBreaks)
  return Math.max(56, Math.min(360, lines * 28 + 32))
}

function writeParallelRows(sheet, startRow, leftRows, rightRows, options = {}) {
  const {
    rightStatRowIndex = -1,
    leftTextRowIndex = -1,
    rightTextRowIndex = -1
  } = options
  const leftMergedWidths = [18, 28, 18, 18]
  const rightMergedWidths = [18, 28, 18, 18]
  let rowNum = startRow
  const maxLen = Math.max(leftRows.length, rightRows.length)
  for (let i = 0; i < maxLen; i++) {
    const left = leftRows[i] || ['']
    const right = rightRows[i] || ['']
    const row = sheet.getRow(rowNum)
    let rowHeight = 22

    if (i === leftTextRowIndex && left[0]) {
      sheet.mergeCells(rowNum, 1, rowNum, 4)
      row.getCell(1).value = left[0]
      row.getCell(1).alignment = { wrapText: true, vertical: 'middle' }
      rowHeight = Math.max(rowHeight, calcMergedTextRowHeight(left[0], leftMergedWidths))
    } else {
      row.getCell(1).value = left[0] || ''
      row.getCell(2).value = left[1] || ''
      row.getCell(3).value = left[2] || ''
      row.getCell(4).value = left[3] || ''
    }

    const rightMerged =
      (i === rightStatRowIndex || i === rightTextRowIndex) && right[0]
    if (rightMerged) {
      sheet.mergeCells(rowNum, 5, rowNum, 8)
      row.getCell(5).value = right[0]
      row.getCell(5).alignment = { wrapText: true, vertical: 'middle' }
      rowHeight = Math.max(rowHeight, calcMergedTextRowHeight(right[0], rightMergedWidths))
    } else {
      row.getCell(5).value = right[0] || ''
      row.getCell(6).value = right[1] || ''
      row.getCell(7).value = right[2] || ''
      row.getCell(8).value = right[3] || ''
    }

    row.eachCell((cell) => setBorder(cell))
    if (rowHeight > 22) row.height = Math.ceil(rowHeight * 1.2)
    rowNum++
  }
  return rowNum
}

function writePeriodSectionTitles(
  sheet,
  rowNum,
  lastLabel,
  thisLabel,
  lastSectionPrefix,
  thisSectionPrefix
) {
  sheet.mergeCells(rowNum, 1, rowNum, 4)
  sheet.mergeCells(rowNum, 5, rowNum, 8)
  const leftTitleCell = sheet.getCell(rowNum, 1)
  const rightTitleCell = sheet.getCell(rowNum, 5)
  leftTitleCell.value = `${lastSectionPrefix}（${lastLabel}）`
  rightTitleCell.value = `${thisSectionPrefix}（${thisLabel}）`
  leftTitleCell.font = { bold: true, size: 12 }
  rightTitleCell.font = { bold: true, size: 12 }
  leftTitleCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  rightTitleCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  setBorder(leftTitleCell)
  setBorder(rightTitleCell)
  sheet.getRow(rowNum).height = 28
}

function writeSectionTitleRow(sheet, rowNum, title) {
  sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
  const cell = sheet.getCell(rowNum, 1)
  cell.value = title
  cell.font = { bold: true, size: 12 }
  cell.alignment = { horizontal: 'center', vertical: 'middle' }
  setBorder(cell)
  return rowNum + 1
}

function writeAuditTableToSheet(sheet, startRow, title, list, formatInspectionOrg, options = {}) {
  const { wideDataColumns = false } = options
  let rowNum = writeSectionTitleRow(sheet, startRow, title)
  const items = Array.isArray(list) ? list : []

  if (!items.length) {
    sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
    sheet.getCell(rowNum, 1).value = '暂无数据'
    setBorder(sheet.getCell(rowNum, 1))
    return rowNum + 2
  }

  const headerRowNum = rowNum
  if (wideDataColumns) {
    sheet.mergeCells(headerRowNum, 2, headerRowNum, 3)
    sheet.mergeCells(headerRowNum, 4, headerRowNum, 5)
    sheet.mergeCells(headerRowNum, 6, headerRowNum, 7)
    sheet.getCell(headerRowNum, 1).value = '序号'
    sheet.getCell(headerRowNum, 2).value = '项目地址'
    sheet.getCell(headerRowNum, 4).value = '设备代码'
    sheet.getCell(headerRowNum, 6).value = '年审时间'
    sheet.getCell(headerRowNum, 8).value = '检验机构'
    for (let col = 1; col <= COL_COUNT; col++) {
      const cell = sheet.getCell(headerRowNum, col)
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
      setBorder(cell)
    }
  } else {
    const headerRow = sheet.getRow(headerRowNum)
    headerRow.values = ['序号', '项目地址', '设备代码', '年审时间', '检验机构', '', '', '']
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber <= 5) {
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
        setBorder(cell)
      }
    })
  }
  rowNum++

  items.forEach((item, index) => {
    if (wideDataColumns) {
      sheet.mergeCells(rowNum, 2, rowNum, 3)
      sheet.mergeCells(rowNum, 4, rowNum, 5)
      sheet.mergeCells(rowNum, 6, rowNum, 7)
      sheet.getCell(rowNum, 1).value = index + 1
      sheet.getCell(rowNum, 2).value = item.name || '-'
      sheet.getCell(rowNum, 4).value = item.deviceCode || '-'
      sheet.getCell(rowNum, 6).value = item.auditTimes || '-'
      sheet.getCell(rowNum, 8).value = formatInspectionOrg
        ? formatInspectionOrg(item.inspectionOrg)
        : (item.inspectionOrg || '-')
      for (let col = 1; col <= COL_COUNT; col++) {
        const cell = sheet.getCell(rowNum, col)
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
        setBorder(cell)
      }
    } else {
      const row = sheet.getRow(rowNum)
      row.getCell(1).value = index + 1
      row.getCell(2).value = item.name || '-'
      row.getCell(3).value = item.deviceCode || '-'
      row.getCell(4).value = item.auditTimes || '-'
      row.getCell(5).value = formatInspectionOrg
        ? formatInspectionOrg(item.inspectionOrg)
        : (item.inspectionOrg || '-')
      row.eachCell((cell, colNumber) => {
        if (colNumber <= 5) {
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
          setBorder(cell)
        }
      })
    }
    rowNum++
  })

  return rowNum + 1
}

function writeManualNoteToSheet(sheet, startRow, title, content) {
  let rowNum = writeSectionTitleRow(sheet, startRow, title)
  sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
  const cell = sheet.getCell(rowNum, 1)
  cell.value = content || ''
  cell.alignment = { wrapText: true, vertical: 'top' }
  setBorder(cell)
  const lineCount = Math.max(3, String(content || '').split('\n').length)
  sheet.getRow(rowNum).height = Math.min(180, Math.max(48, lineCount * 18 + 12))
  return rowNum + 2
}

function writeSafetyEvaluationToSheet(sheet, startRow, title, selectedValue, options) {
  let rowNum = writeSectionTitleRow(sheet, startRow, title)
  ;(options || []).forEach((opt) => {
    sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
    const cell = sheet.getCell(rowNum, 1)
    const mark = selectedValue === opt.value ? '✓' : '☐'
    cell.value = `${mark}  ${opt.label}`
    cell.alignment = { wrapText: true, vertical: 'middle' }
    setBorder(cell)
    sheet.getRow(rowNum).height = Math.max(22, Math.ceil(opt.label.length / 40) * 18 + 8)
    rowNum++
  })
  return rowNum + 1
}

function writeWeeklySafetyInputSections(sheet, startRow, safetyInputs) {
  let rowNum = startRow

  WEEKLY_SAFETY_NOTE_FIELDS.forEach((field) => {
    rowNum = writeManualNoteToSheet(
      sheet,
      rowNum,
      field.label,
      safetyInputs?.[field.key] || ''
    )
  })

  rowNum = writeSafetyEvaluationToSheet(
    sheet,
    rowNum,
    WEEKLY_SAFETY_EVALUATION_TITLE,
    safetyInputs?.safetyEvaluation,
    WEEKLY_SAFETY_EVALUATION_OPTIONS
  )

  return rowNum
}

function writeMonthlyDispatchExtraSections(
  sheet,
  startRow,
  {
    monthAuditList,
    monthShiyan125List,
    nextMonthAuditList,
    nextShiyan125List,
    manualInputs,
    manualNoteFields,
    formatInspectionOrg
  }
) {
  let rowNum = startRow
  rowNum = writeAuditTableToSheet(
    sheet,
    rowNum,
    '当月电梯年审项目',
    monthAuditList,
    formatInspectionOrg,
    { wideDataColumns: true }
  )
  rowNum = writeAuditTableToSheet(
    sheet,
    rowNum,
    '当月125%载荷试验项目',
    monthShiyan125List,
    formatInspectionOrg,
    { wideDataColumns: true }
  )
  rowNum = writeAuditTableToSheet(
    sheet,
    rowNum,
    '下月电梯年审项目',
    nextMonthAuditList,
    formatInspectionOrg,
    { wideDataColumns: true }
  )
  rowNum = writeAuditTableToSheet(
    sheet,
    rowNum,
    '下月125%载荷试验项目',
    nextShiyan125List,
    formatInspectionOrg,
    { wideDataColumns: true }
  )

  ;(manualNoteFields || []).forEach((field) => {
    rowNum = writeManualNoteToSheet(
      sheet,
      rowNum,
      field.label,
      manualInputs?.[field.key] || ''
    )
  })

  return rowNum
}

async function addSignatureImage(workbook, sheet, url, col, rowNum, width, height) {
  if (!url) return false
  try {
    const imageBuffer = await fetchImageBuffer(url)
    const imageId = workbook.addImage({
      buffer: imageBuffer,
      extension: getImageExtension(url)
    })
    sheet.addImage(imageId, {
      tl: { col, row: rowNum - 1 },
      ext: { width, height }
    })
    return true
  } catch (e) {
    sheet.getCell(rowNum, col + 1).value = url
    setBorder(sheet.getCell(rowNum, col + 1))
    return false
  }
}

/**
 * 按周排查页面模板导出 Excel
 */
export async function exportWeeklyInspectionReport({
  formattedWeekRange,
  currentTime,
  showDateInTitle = true,
  lastWeekLabel,
  thisWeekLabel,
  oldEventStr,
  lastWeekEvents,
  eventStr,
  thisWeekEvents,
  thisWeekEventStats,
  workOrderRecords,
  lastWeekWorkOrderRecords,
  thisWeekWorkOrderRecords,
  staffSignList,
  formatEventDisplay,
  filename,
  reportTitle = '海康智慧电梯监测周排查',
  lastSectionPrefix = '上周历史触发',
  thisSectionPrefix = '本周历史触发统计',
  thisPeriodTotalLabel = '本周触发总数',
  emptyThisPeriodText = '本周暂无触发记录',
  monthlyDispatchExtras,
  safetyInputs,
  lastWorkOrderSectionPrefix = '上周工单未处理记录',
  thisWorkOrderSectionPrefix = '本周工单未处理记录'
}) {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet(reportTitle)

  sheet.columns = [
    { width: 18 },
    { width: 28 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 28 },
    { width: 18 },
    { width: 18 }
  ]

  let rowNum = 1
  const titleText = showDateInTitle && formattedWeekRange
    ? `${reportTitle} （${formattedWeekRange}）`
    : reportTitle

  if (currentTime) {
    sheet.mergeCells(rowNum, 1, rowNum + 1, 7)
    const titleCell = sheet.getCell(rowNum, 1)
    titleCell.value = titleText
    titleCell.font = { size: 20, bold: true }
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
    setBorder(titleCell)

    sheet.getCell(rowNum, 8).value = '系统时间'
    sheet.getCell(rowNum, 8).font = { bold: true, size: 11 }
    sheet.getCell(rowNum, 8).alignment = { horizontal: 'center', vertical: 'middle' }
    setBorder(sheet.getCell(rowNum, 8))

    sheet.getCell(rowNum + 1, 8).value = currentTime
    sheet.getCell(rowNum + 1, 8).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    setBorder(sheet.getCell(rowNum + 1, 8))

    sheet.getRow(rowNum).height = 24
    sheet.getRow(rowNum + 1).height = 24
  } else {
    sheet.mergeCells(rowNum, 1, rowNum + 1, COL_COUNT)
    const titleCell = sheet.getCell(rowNum, 1)
    titleCell.value = titleText
    titleCell.font = { size: 20, bold: true }
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
    sheet.getRow(rowNum).height = 28
    sheet.getRow(rowNum + 1).height = 28
  }
  rowNum += 3

  writePeriodSectionTitles(
    sheet,
    rowNum,
    lastWeekLabel,
    thisWeekLabel,
    lastSectionPrefix,
    thisSectionPrefix
  )
  rowNum++

  const leftRows = buildEventSectionRows({
    textStr: oldEventStr,
    events: lastWeekEvents,
    formatEventDisplay,
    emptyText: '无'
  })
  const rightRows = buildThisWeekSectionRows({
    eventStr,
    thisWeekEvents,
    thisWeekEventStats,
    formatEventDisplay,
    thisPeriodTotalLabel,
    emptyThisPeriodText
  })
  const leftTextRowIndex = oldEventStr ? 0 : -1
  const rightTextRowIndex = eventStr ? 0 : -1
  const rightStatRowIndex =
    !eventStr && thisWeekEvents && thisWeekEvents.length ? 0 : -1
  const eventsContentStartRow = rowNum
  rowNum = writeParallelRows(sheet, rowNum, leftRows, rightRows, {
    leftTextRowIndex,
    rightTextRowIndex,
    rightStatRowIndex
  })
  rowNum++

  if (monthlyDispatchExtras) {
    rowNum = writeMonthlyDispatchExtraSections(sheet, rowNum, monthlyDispatchExtras)
  }

  if (safetyInputs) {
    rowNum = writeWeeklySafetyInputSections(sheet, rowNum, safetyInputs)
  }

  const useSplitWorkOrders =
    lastWeekWorkOrderRecords !== undefined || thisWeekWorkOrderRecords !== undefined

  if (useSplitWorkOrders) {
    rowNum = writeParallelWorkOrderSections(
      sheet,
      rowNum,
      lastWeekLabel,
      thisWeekLabel,
      lastWeekWorkOrderRecords || [],
      thisWeekWorkOrderRecords || [],
      lastWorkOrderSectionPrefix,
      thisWorkOrderSectionPrefix
    )
  } else {
    sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
    sheet.getCell(rowNum, 1).value = '工单记录'
    sheet.getCell(rowNum, 1).font = { bold: true, size: 12 }
    sheet.getCell(rowNum, 1).alignment = { horizontal: 'center' }
    rowNum++

    if (workOrderRecords && workOrderRecords.length) {
      const headerRowNum = rowNum
      sheet.mergeCells(headerRowNum, 1, headerRowNum, 2)
      sheet.mergeCells(headerRowNum, 3, headerRowNum, 4)
      sheet.getCell(headerRowNum, 1).value = '生成时间'
      sheet.getCell(headerRowNum, 3).value = '项目地址'
      sheet.getCell(headerRowNum, 5).value = '记录类型'
      sheet.getCell(headerRowNum, 6).value = '紧急程度'
      sheet.getCell(headerRowNum, 7).value = '处理状态'
      sheet.getRow(headerRowNum).eachCell((cell, colNumber) => {
        if (colNumber <= 7) {
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
          setBorder(cell)
        }
      })
      rowNum++

      workOrderRecords.forEach((order) => {
        sheet.mergeCells(rowNum, 1, rowNum, 2)
        sheet.mergeCells(rowNum, 3, rowNum, 4)
        sheet.getCell(rowNum, 1).value = order.createTime || '-'
        sheet.getCell(rowNum, 3).value = order.communityAddress || order.areaId || '-'
        sheet.getCell(rowNum, 5).value = order.orderType || '-'
        sheet.getCell(rowNum, 6).value = order.urgency || '-'
        sheet.getCell(rowNum, 7).value = order.status || '-'
        sheet.getRow(rowNum).eachCell((cell, colNumber) => {
          if (colNumber <= 7) {
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
            setBorder(cell)
          }
        })
        rowNum++
      })
    } else {
      sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
      sheet.getCell(rowNum, 1).value = '无'
      setBorder(sheet.getCell(rowNum, 1))
      rowNum++
    }
  }
  const workOrdersEndRow = rowNum - 1
  rowNum++

  sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
  sheet.getCell(rowNum, 1).value = '签字确认'
  sheet.getCell(rowNum, 1).font = { bold: true, size: 12 }
  sheet.getCell(rowNum, 1).alignment = { horizontal: 'center' }
  rowNum++

  const signs = Array.isArray(staffSignList) ? staffSignList : []
  if (!signs.length) {
    sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
    sheet.getCell(rowNum, 1).value = '暂无签字信息'
    setBorder(sheet.getCell(rowNum, 1))
    rowNum++
  } else {
    for (let i = 0; i < signs.length; i += 2) {
      const left = signs[i]
      const right = signs[i + 1]

      if (right) {
        sheet.mergeCells(rowNum, 1, rowNum, 4)
        sheet.mergeCells(rowNum, 5, rowNum, 8)
        sheet.getCell(rowNum, 1).value = left.title || '手写签名'
        sheet.getCell(rowNum, 5).value = right.title || '手写签名'
      } else {
        sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT)
        sheet.getCell(rowNum, 1).value = left.title || '手写签名'
      }
      sheet.getCell(rowNum, 1).font = { bold: true }
      if (right) sheet.getCell(rowNum, 5).font = { bold: true }
      rowNum++

      const signatureRowNum = rowNum
      if (right) {
        sheet.mergeCells(signatureRowNum, 1, signatureRowNum, 4)
        sheet.mergeCells(signatureRowNum, 5, signatureRowNum, 8)
        setBorder(sheet.getCell(signatureRowNum, 1))
        setBorder(sheet.getCell(signatureRowNum, 5))
        if (!left.staffImage) sheet.getCell(signatureRowNum, 1).value = '暂无签名'
        if (!right.staffImage) sheet.getCell(signatureRowNum, 5).value = '暂无签名'
        sheet.getRow(signatureRowNum).height = 56
        await addSignatureImage(workbook, sheet, left.staffImage, 0.5, signatureRowNum, 140, 50)
        await addSignatureImage(workbook, sheet, right.staffImage, 4.5, signatureRowNum, 140, 50)
      } else {
        sheet.mergeCells(signatureRowNum, 1, signatureRowNum, COL_COUNT)
        setBorder(sheet.getCell(signatureRowNum, 1))
        if (!left.staffImage) sheet.getCell(signatureRowNum, 1).value = '暂无签名'
        sheet.getRow(signatureRowNum).height = 56
        await addSignatureImage(workbook, sheet, left.staffImage, 2.5, signatureRowNum, 140, 50)
      }
      rowNum += 2
    }
  }

  let sealAdded = false
  try {
    ensureMinimumAreaHeight(
      sheet,
      eventsContentStartRow,
      workOrdersEndRow,
      SEAL_HEIGHT_PX + 20
    )
    sealAdded = await addCenteredSeal(workbook, sheet, {
      startCol: 1,
      endCol: COL_COUNT,
      startRow: eventsContentStartRow,
      endRow: workOrdersEndRow
    })
  } catch (e) {
    console.warn('印章加载失败', e)
  }

  let buffer = await workbook.xlsx.writeBuffer()
  if (sealAdded) {
    buffer = await patchLastImageAnchor(buffer, SEAL_WIDTH_PX, SEAL_HEIGHT_PX)
  }
  downloadWorkbook(buffer, filename || `${reportTitle}.xlsx`)
}

/** 月调度报表导出（复用周排查模板） */
export function exportMonthlyInspectionReport(params) {
  const {
    nextMonthAuditList,
    monthAuditList,
    monthShiyan125List,
    nextShiyan125List,
    manualInputs,
    manualNoteFields,
    formatInspectionOrg,
    ...rest
  } = params

  return exportWeeklyInspectionReport({
    reportTitle: '海康智慧电梯监测月调度',
    lastSectionPrefix: '上月历史触发',
    thisSectionPrefix: '本月历史触发统计',
    thisPeriodTotalLabel: '本月触发总数',
    emptyThisPeriodText: '本月暂无触发记录',
    lastWorkOrderSectionPrefix: '上月工单未处理记录',
    thisWorkOrderSectionPrefix: '本月工单未处理记录',
    monthlyDispatchExtras: {
      monthAuditList,
      monthShiyan125List,
      nextMonthAuditList,
      nextShiyan125List,
      manualInputs,
      manualNoteFields,
      formatInspectionOrg
    },
    ...rest
  })
}
